import { useEffect, useMemo } from "react";
import { Button, Checkbox, Dropdown, Loader } from "../utils";
import { useGetUsersMutation } from "../../api/userSlice";
import { useGetPlantUsersMutation } from "../../api/vmsSlice";
import { useAllPermissionSet, useUserDetail } from "../../services/hooks";
import { capitalizeCamelCase } from "../../services/functions";
import { buttonClasses } from "../utils/theme";
import "../Users/AddUser.css";
import { AccordionWrapperVanilla, CHECKPOINT_CHILD_PERMS, getWarehousePermissions } from "../Users/permissionHelpers";

// "" is the checkpoint access itself, the rest are its child permissions.
const CHECKPOINT_PERMS = ["", ...CHECKPOINT_CHILD_PERMS];

const userLabel = (user) => (user.email ? `${user.name} (${user.email})` : user.name);
const trueKeys = (obj = {}) => Object.keys(obj).filter((key) => obj[key]);

/**
 * A selection is { checkpoints: { [checkpointUid]: { [perm]: true } }, warehouse: { [perm]: true } }.
 * userIds + checkpoints + warehouse: new users, all getting the same selection.
 * access: edited selections of users who already have access (update only), by userId. An empty selection removes access.
 */
const emptySelection = () => ({ checkpoints: {}, warehouse: {} });
export const emptyUserAssignment = () => ({ userIds: [], ...emptySelection(), access: {} });

// With `checkpoints` (plant being created) a checkpoint is sent by index; without them uids are real checkpoint ids.
function toCheckpointPermissions(selection, checkpoints) {
	const list = checkpoints
		? checkpoints.map((cp, index) => ({ index, permissions: CHECKPOINT_PERMS.filter((perm) => selection[cp.uid]?.[perm]) }))
		: Object.entries(selection).map(([checkpointId, perms]) => ({ checkpointId, permissions: CHECKPOINT_PERMS.filter((perm) => perms[perm]) }));
	return list.filter((cp) => cp.permissions.length);
}

const toPermissionsPayload = (selection, checkpoints) => ({
	checkpointPermissions: toCheckpointPermissions(selection.checkpoints, checkpoints),
	warehousePermissions: trueKeys(selection.warehouse),
});

/**
 * Converts the state of <PlantUserPermissions /> into the `assignUsers` payload (one selection applied to all selected users).
 * Pass `checkpoints` (exact list and order sent to the API) when creating a plant; omit it when updating.
 */
export function buildAssignUsersPayload(assignment, checkpoints) {
	return { userIds: assignment.userIds, ...toPermissionsPayload(assignment, checkpoints) };
}

// update-plant `userAccess` payload: only users whose access was edited or removed. No permissions left removes access.
export function buildUserAccessPayload(assignment) {
	return Object.entries(assignment.access).map(([userId, selection]) => ({ userId, ...toPermissionsPayload(selection) }));
}

// Selection of a user from the get-plant-users response. Warehouse keys the editor can't see are kept so they are sent back unchanged.
const fromPlantUser = (user) => ({
	checkpoints: Object.fromEntries(user.checkpointPermissions.map((cp) => [cp.checkpointId, Object.fromEntries(cp.permissions.map((perm) => [perm, true]))])),
	warehouse: Object.fromEntries((user.warehousePermissions || []).map((perm) => [perm, true])),
});
const hasAnyPermission = (selection) =>
	Object.values(selection.checkpoints).some((perms) => Object.values(perms).some(Boolean)) || Object.values(selection.warehouse).some(Boolean);

/**
 * Assign users while adding a warehouse; with `plantId` (update) it also lists users who already have access
 * so their checkpoint and warehouse permissions can be changed or removed.
 */
function PlantUserPermissions({ orgId, plantId, checkpoints = [], value = emptyUserAssignment(), onChange }) {
	const { userDetail } = useUserDetail();

	const [getUsers, { data: usersData, isLoading: isUsersLoading }] = useGetUsersMutation({ fixedCacheKey: "plantAssignUsers" });
	const [getPlantUsers, { data: plantUsersData, isLoading: isPlantUsersLoading }] = useGetPlantUsersMutation();
	// Only permissions the editor holds are returned, so only those can be given from here.
	const { permissions: permissionSetPerms } = useAllPermissionSet();
	const warehousePermissions = getWarehousePermissions(permissionSetPerms);

	useEffect(() => {
		if (orgId) getUsers({ orgId, pagesize: 1000 });
	}, [orgId]);

	useEffect(() => {
		if (plantId) getPlantUsers({ plantId });
	}, [plantId]);

	// On update the plant's real checkpoints are used (uid = checkpoint id).
	const plantCheckpoints = plantId ? (plantUsersData?.data?.checkpoints || []).map((cp) => ({ uid: cp._id, displayName: cp.displayName })) : checkpoints;
	const plantUsers = plantId ? plantUsersData?.data?.users || [] : [];
	// Users at or above the editor's role (and the editor) are not listed; they also can't be added again below.
	const existingUsers = plantUsers.filter((user) => user.canEdit);

	const currentUserId = userDetail?._id || userDetail?.userId;
	// Rebuilt only when the user lists change, not on every keystroke of the plant form.
	const userOptions = useMemo(() => {
		if (!orgId) return [];
		const existingUserIds = new Set(plantUsers.map((user) => user.userId));
		return (usersData?.data || []).filter((user) => user._id != currentUserId && !existingUserIds.has(user._id)).map((user) => ({ label: userLabel(user), value: user._id }));
	}, [orgId, usersData, plantUsersData, currentUserId]);
	const selectedOptions = useMemo(() => {
		const optionsById = new Map(userOptions.map((opt) => [opt.value, opt]));
		return value.userIds.map((userId) => optionsById.get(userId) || { label: userId, value: userId });
	}, [userOptions, value.userIds]);

	const setExistingUserSelection = (userId, selection) => onChange({ ...value, access: { ...value.access, [userId]: selection } });
	const resetExistingUser = (userId) => {
		const { [userId]: _, ...access } = value.access;
		onChange({ ...value, access });
	};

	return (
		<div className="theme-container">
			<h6>{plantId ? "Users" : "Assign Users"}</h6>

			{plantId ? (
				<div style={{ marginBottom: "1.5rem" }}>
					<div style={{ fontWeight: "600", margin: "0.75rem 0 0.5rem" }}>Users with access</div>
					{isPlantUsersLoading ? <Loader size="1rem" /> : null}
					{!isPlantUsersLoading && !existingUsers.length ? <p style={{ color: "#727376" }}>No users have access to this warehouse yet.</p> : null}
					{existingUsers.map((user) => {
						const edited = value.access[user.userId];
						const removing = edited && !hasAnyPermission(edited);
						const title = (
							<>
								{userLabel(user)}
								{removing ? <span style={{ color: "#d32f2f", marginLeft: "0.5rem" }}>— access will be removed</span> : edited ? <span style={{ color: "#ff7200", marginLeft: "0.5rem" }}>— edited</span> : null}
							</>
						);
						return (
							<AccordionWrapperVanilla key={user.userId} title={title}>
								<PermissionChecklist
									checkpoints={plantCheckpoints}
									warehousePermissions={warehousePermissions}
									selection={edited || fromPlantUser(user)}
									onChange={(next) => setExistingUserSelection(user.userId, next)}
								/>
								<div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
									{!removing ? <Button text="Remove access" style={buttonClasses.lynkitOrangeEmpty} onClick={() => setExistingUserSelection(user.userId, emptySelection())} /> : null}
									{edited ? <Button text="Undo changes" style={buttonClasses.lynkitOrangeEmpty} onClick={() => resetExistingUser(user.userId)} /> : null}
								</div>
							</AccordionWrapperVanilla>
						);
					})}
					<div style={{ fontWeight: "600", margin: "1.5rem 0 0.5rem" }}>Add users</div>
				</div>
			) : null}

			<p style={{ color: "#727376", fontSize: "0.85rem", margin: "0.25rem 0 0.75rem" }}>
				Selected users get access to this warehouse with the permissions chosen below.
			</p>
			{!orgId ? (
				<p style={{ color: "#727376" }}>Select an organization to assign users.</p>
			) : (
				<div style={{ maxWidth: "600px" }}>
					<Dropdown
						id="assignUsers"
						name="assignUsers"
						label="Users"
						multiple
						disableCloseOnSelect
						loading={isUsersLoading}
						options={userOptions}
						value={selectedOptions}
						onChange={(options) => onChange({ ...value, userIds: (options || []).map((opt) => opt.value) })}
					/>
				</div>
			)}

			{selectedOptions.length > 0 && (
				<div style={{ marginTop: "1rem" }}>
					<p style={{ color: "#727376", fontSize: "0.85rem", marginBottom: "0.75rem" }}>
						These permissions will be applied to all {selectedOptions.length} selected user{selectedOptions.length > 1 ? "s" : ""}.
					</p>
					<PermissionChecklist
						checkpoints={plantCheckpoints}
						warehousePermissions={warehousePermissions}
						selection={value}
						onChange={(selection) => onChange({ ...value, ...selection })}
					/>
				</div>
			)}
		</div>
	);
}

// Checkpoint and warehouse permission checkboxes for one selection.
function PermissionChecklist({ checkpoints, warehousePermissions, selection, onChange }) {
	if (!checkpoints.length && !warehousePermissions.length) return null;

	const setCheckpoints = (next) => onChange({ checkpoints: next, warehouse: selection.warehouse });
	const setWarehouse = (next) => onChange({ checkpoints: selection.checkpoints, warehouse: next });

	const setCheckpointPerm = (uid, perm, checked) => {
		const current = { ...selection.checkpoints[uid] };
		if (perm === "") {
			// parent checkbox toggles the checkpoint and all its child permissions
			CHECKPOINT_PERMS.forEach((p) => (current[p] = checked));
		} else {
			current[perm] = checked;
		}
		setCheckpoints({ ...selection.checkpoints, [uid]: current });
	};
	const setAllCheckpoints = (checked) => setCheckpoints(Object.fromEntries(checkpoints.map((cp) => [cp.uid, Object.fromEntries(CHECKPOINT_PERMS.map((p) => [p, checked]))])));
	const allCheckpointsChecked = checkpoints.every((cp) => CHECKPOINT_PERMS.every((p) => selection.checkpoints[cp.uid]?.[p]));

	// Select All only touches the listed permissions; keys the editor can't see stay as they are.
	const setAllWarehouse = (checked) => setWarehouse({ ...selection.warehouse, ...Object.fromEntries(warehousePermissions.map((perm) => [perm, checked])) });
	const allWarehouseChecked = warehousePermissions.every((perm) => selection.warehouse[perm]);

	return (
		<div className="plant-permissions-section">
			{checkpoints.length > 0 && (
				<div className="checkpoint-perm-wrap-outer">
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						<div style={{ fontWeight: "600" }}>Checkpoint Permissions</div>
						<Checkbox label="Select All" checked={allCheckpointsChecked} onChange={(e) => setAllCheckpoints(e.target.checked)} />
					</div>
					{checkpoints.map((cp) => (
						<div key={cp.uid} className="checkpoint-perm-wrap">
							<Checkbox label={cp.displayName} checked={!!selection.checkpoints[cp.uid]?.[""]} onChange={(e) => setCheckpointPerm(cp.uid, "", e.target.checked)} />
							<div className="checkpoint-perm-wrap-inner" style={{ display: "inline-flex", flexWrap: "wrap" }}>
								{CHECKPOINT_CHILD_PERMS.map((perm) => (
									<Checkbox
										key={perm}
										label={capitalizeCamelCase(perm)}
										checked={!!selection.checkpoints[cp.uid]?.[perm]}
										onChange={(e) => setCheckpointPerm(cp.uid, perm, e.target.checked)}
									/>
								))}
							</div>
						</div>
					))}
				</div>
			)}

			{warehousePermissions.length > 0 && (
				<>
					<div style={{ display: "flex", justifyContent: "space-between", marginTop: "1.5rem" }}>
						<div style={{ fontWeight: "600" }}>Warehouse Permissions</div>
						<Checkbox label="Select All" checked={allWarehouseChecked} onChange={(e) => setAllWarehouse(e.target.checked)} />
					</div>
					<div className="perm_wrap grid grid-cols-2 gap-4">
						{warehousePermissions.map((perm) => (
							<Checkbox
								key={perm}
								label={capitalizeCamelCase(perm)}
								checked={!!selection.warehouse[perm]}
								onChange={(e) => setWarehouse({ ...selection.warehouse, [perm]: e.target.checked })}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
}

export default PlantUserPermissions;
